import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

const kindColors = {
  scalar: 'default',
  object: 'info',
  array: 'secondary',
};

const FieldLegoChip = ({ node, selected, onSelect, disabled }) => {
  const dragId = `erp:${node.path}`;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId,
    data: { type: 'erp', path: node.path, kind: node.kind },
    disabled,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const isDraggable = node.kind === 'scalar' || node.kind === 'array';

  return (
    <Box
      sx={{
        py: 0.25,
        pl: node.depth ? node.depth * 1.5 : 0,
      }}
    >
      <Box
        ref={isDraggable ? setNodeRef : undefined}
        style={isDraggable ? style : undefined}
        {...(isDraggable ? listeners : {})}
        {...(isDraggable ? attributes : {})}
        onClick={() => {
          if (!disabled && isDraggable) {
            onSelect?.(node);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          flex: 1,
          cursor: disabled || !isDraggable ? 'default' : 'grab',
          borderRadius: 1,
          border: '1px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          bgcolor: selected ? 'action.selected' : 'background.default',
          px: 1,
          py: 0.75,
          touchAction: isDraggable ? 'none' : 'auto',
          '&:active': isDraggable && !disabled ? { cursor: 'grabbing' } : undefined,
        }}
      >
        {isDraggable && (
          <DragIndicatorIcon
            fontSize="small"
            sx={{ mt: 0.75, color: 'text.secondary', flexShrink: 0, pointerEvents: 'none' }}
          />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={node.kind} size="small" color={kindColors[node.kind] || 'default'} variant="outlined" />
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {node.path}
            </Typography>
            {node.itemField && node.arrayRootPath && (
              <Chip
                label={`${node.arrayRootPath.replace(/^\$\./, '')}[].${node.key}`}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
          {node.sample && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {node.sample}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FieldLegoChip;
